// Labels that depend on the connection type (HTML updateThreeLabels / updateCrumb), translated.
import { useMemo } from "react";
import useI18n from "../../../hooks/useTranslations";
import type { ScrewConnector3DLabels } from "./view3dLabels";
import type { InputSectionId } from "./types";
import {
  isPanel3,
  isPanelD,
  isPanelMat,
  isSteel3,
  isThreeEl,
  type ConnectionType,
} from "../../../http/screwConnector/formulas";

export type ThreeLabels = {
  pageTitle: string;
  sectionElement1: string;
  sectionElement2: string;
  fiber2Unit: string;
  eps2Unit: string;
  eps2Element: string;
  alpha1Element: string;
  alpha2Element: string;
  colsLabel: string;
  s2Unit: string;
  positionMeta: string;
  legendElement1: string;
  legendElement2: string;
  viewHintSubject: string;
};

export function useThreeLabels(connectionType: ConnectionType): ThreeLabels {
  const t = useI18n().translation.screwConnectorModule.calculation;
  return useMemo(() => {
    const three = isThreeEl(connectionType);
    const s3 = isSteel3(connectionType);
    const panel3 = isPanel3(connectionType);
    const panelD = isPanelD(connectionType);
    const panel = isPanelMat(connectionType);
    const typeLabel = t.typeSection.types[connectionType] ?? "";
    const pick = <T>(steel3Value: T, threeValue: T, defaultValue: T) => (s3 ? steel3Value : three ? threeValue : defaultValue);
    return {
      pageTitle: typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1),
      sectionElement1: pick(
        panel3 ? t.sections.element13Panel : t.sections.element13Steel,
        panelD ? t.sections.element1PanelCentral : t.sections.element1Central,
        panel ? t.sections.element1Panel : t.sections.element1,
      ),
      sectionElement2: pick(t.sections.element2Central, t.sections.element23, t.sections.element2),
      fiber2Unit: three ? t.element.fiberUnit23 : t.element.fiberUnit2,
      eps2Unit: three ? t.element.epsUnit23 : t.element.epsUnit2,
      eps2Element: three ? t.elementRefs.elements23 : t.elementRefs.element2,
      alpha1Element: pick(
        panel3 ? t.elementRefs.elements13Panel : t.elementRefs.elements13Steel,
        t.elementRefs.element1Central,
        t.elementRefs.element1,
      ),
      alpha2Element: three ? t.elementRefs.elements23 : s3 ? t.elementRefs.element2Central : t.elementRefs.element2,
      colsLabel: three ? t.gridSection.cols23 : t.gridSection.cols2,
      s2Unit: three ? t.gridSection.s2Unit23 : t.gridSection.s2Unit2,
      positionMeta: pick(
        panel3 ? t.positioning.metaPanel3 : t.positioning.metaSteel3,
        t.positioning.metaThree,
        t.positioning.meta,
      ),
      legendElement1: pick(
        panel3 ? t.view.legend.el13Panel : t.view.legend.el13Steel,
        panelD ? t.view.legend.el1PanelCentral : t.view.legend.el1Central,
        panel ? t.view.legend.el1Panel : t.view.legend.el1,
      ),
      legendElement2: pick(t.view.legend.el2Central, t.view.legend.el23, t.view.legend.el2),
      viewHintSubject: pick(
        panel3 ? t.view.hintSubjects.panel3 : t.view.hintSubjects.steel3,
        t.view.hintSubjects.three,
        panel ? t.view.hintSubjects.panel : t.view.hintSubjects.two,
      ),
    };
  }, [connectionType, t]);
}

// Translated labels of the 3D viewer's hint and legend (ScrewConnector3DView `labels`).
export function useView3DLabels(connectionType: ConnectionType): ScrewConnector3DLabels {
  const t = useI18n().translation.screwConnectorModule.calculation.view;
  const labels = useThreeLabels(connectionType);
  return useMemo(
    () => ({
      hintControls: t.hintControls,
      hintSubject: labels.viewHintSubject,
      hintTail: t.hintTail,
      element1: labels.legendElement1,
      element2: labels.legendElement2,
      menuElement1: isSteel3(connectionType) ? t.toolbar.elements.elements13 : t.toolbar.elements.element1,
      menuElement2: isThreeEl(connectionType) ? t.toolbar.elements.elements23 : t.toolbar.elements.element2,
      screwOk: t.legend.screwOk,
      screwBad: t.legend.screwBad,
      dims: t.legend.dims,
      fn: t.legend.fn,
      fv: t.legend.fv,
      fa: t.legend.fa,
    }),
    [t, labels, connectionType],
  );
}

// Titles of the input panels (sidebar, "missing fields" links of the solutions card).
export function useSectionTitles(connectionType: ConnectionType): Record<InputSectionId, string> {
  const t = useI18n().translation.screwConnectorModule.calculation.sections;
  const labels = useThreeLabels(connectionType);
  return useMemo(
    () => ({
      secTipologia: t.tipologia,
      secEl1: labels.sectionElement1,
      secEl2: labels.sectionElement2,
      secCarichi: t.loads,
      secConnessione: t.connection,
      secGriglia: t.grid,
    }),
    [t, labels],
  );
}
